import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaSearch, FaShoppingCart, FaHeart } from 'react-icons/fa'; // Make sure to import necessary icons
import translations from '../utils/data';
import banner from '../assets/main/banner6.webp'
import { Helmet } from 'react-helmet';
import BusinessSchema from '../components/BusinessSchema';

const Store = ({ category }) => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [wishlist, setWishlist] = useState([]);
    const { products } = translations;
    
    // Filter products based on selected category and search
    const filteredProducts = selectedCategory === 'all'
        ? products.product.filter(product => 
            product.name.toLowerCase().includes(searchQuery.toLowerCase()))
        : products.product.filter(product => 
            product.category === selectedCategory && 
            product.name.toLowerCase().includes(searchQuery.toLowerCase()));


    // Add this style for animations
    React.useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            @keyframes bounce-subtle {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-3px); }
            }
            .bounce-animation {
                animation: bounce-subtle 2s infinite;
            }
            
            @keyframes pulse-badge {
                0% { box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.7); }
                70% { box-shadow: 0 0 0 6px rgba(249, 115, 22, 0); }
                100% { box-shadow: 0 0 0 0 rgba(249, 115, 22, 0); }
            }
            .pulse-badge {
                animation: pulse-badge 2s infinite;
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    const SITE_URL = 'https://sacredrelm.com';

    // Organization Schema
    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        "name": "Sacred Relm",
        "url": SITE_URL
    };

    // CollectionPage Schema
    const collectionPageSchema = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": category ? `${category} Products` : "All Spiritual Products",
        "description": `Browse our collection of ${category || 'spiritual'} products including Karungali items and sacred accessories.`,
        "url": `${SITE_URL}/shop${category ? `?category=${category}` : ''}`,
        "publisher": {
        "@id": `${SITE_URL}/#organization`
        }
    };

    // ItemList Schema for Products
    const itemListSchema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "itemListElement": translations.products.product
        .filter(product => !category || product.category === category)
        .slice(0, 10) // Limit to first 10 products
        .map((product, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "url": `${SITE_URL}/product/${product.id}`,
            "name": product.name,
            "image": product.photo.image1
        }))
    };

    // Breadcrumb Schema
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
        {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": SITE_URL
        },
        {
            "@type": "ListItem",
            "position": 2,
            "name": category || "Shop",
            "item": `${SITE_URL}/shop${category ? `?category=${category}` : ''}`
        }
        ]
    };

    return (
        <div className="bg-[#faebd7] min-h-screen">
            <Helmet>
                <title>Shop Spiritual Items – Sacred Relm Store</title>
                <meta name="description" content="Browse our premium collection of spiritual products, idols, yantras, and sacred artifacts. Fast delivery across India." />
                <link rel="canonical" href={`${SITE_URL}/shop`} />
                  {/* Organization Schema */}
                <script type="application/ld+json">
                {JSON.stringify(organizationSchema)}
                </script>
                
                {/* CollectionPage Schema */}
                <script type="application/ld+json">
                {JSON.stringify(collectionPageSchema)}
                </script>
                
                {/* ItemList Schema */}
                <script type="application/ld+json">
                {JSON.stringify(itemListSchema)}
                </script>
                
                {/* Breadcrumb Schema */}
                <script type="application/ld+json">
                {JSON.stringify(breadcrumbSchema)}
                </script>
            </Helmet>
              <BusinessSchema pageType="store" />
            {/* Spiritual Hero Banner */}
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center opacity-20" 
                     style={{backgroundImage: `url(${banner})`}}></div>
                <div className="relative py-20 px-4 text-center">
                    <h1 className="text-4xl md:text-6xl font-serif text-[#614E42] mb-4">Spiritual Heritage Series</h1>
                    <p className="text-xl md:text-2xl text-[#614E42]  font-light">
                        Discover products that elevate your spiritual journey
                    </p>
                    
                    {/* Search Bar */}
                    <div className=" mt-8 relative">
                        <input
                            type="text"
                            placeholder="Search for spiritual items..."
                            className="w-full py-3 px-5 pr-12 rounded-full bg-white/80 backdrop-blur-sm border border-[#D8C3A5] text-[#614E42] focus:outline-none focus:ring-2 focus:ring-[#8E9775]"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <FaSearch className="absolute right-4 top-4 text-[#8E9775]" />
                    </div>
                </div>
            </div>
            
            {/* Store Content */}
            <div className="px-4 py-12">
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filteredProducts.length > 0 ? filteredProducts.map((product) => (
                        <Link to={`/product/${product.id}`} key={product.id} className="group block h-full">
                            <div className="bg-white/80 backdrop-blur-sm overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-[#E0DBCF] flex flex-col h-full">
                                {/* Image Container - Responsive with aspect ratio */}
                                <div className="relative overflow-hidden aspect-[4/3] sm:aspect-[3/2] md:aspect-[4/3] flex-shrink-0">
                                    <img
                                        src={product.photo.image1}
                                        alt={product.name}
                                        className="w-full h-full object-contain transform group-hover:scale-110 transition-transform duration-700 ease-in-out"
                                    />
                                    
                                    {/* Sale & New Tags */}
                                    <div className="absolute top-3 left-3 flex flex-col gap-2">
                                        {product.discount > 0 && (
                                            <span className="bg-[#514B60] text-white text-xs font-bold px-3 py-1 rounded-full">
                                                {product.discount}% OFF
                                            </span>
                                        )}
                                        {product.freeAccessories && product.freeAccessories.length > 0 && (
                                            <div className="bg-orange-500/90 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs flex items-center pulse-badge">
                                                <span className="mr-1">🎁</span> 
                                                {product.freeAccessories.length > 1 ? 
                                                    `${product.freeAccessories.length} GIFTS` : 
                                                    'FREE GIFT'
                                                }
                                            </div>
                                        )}
                                    </div>

                                    {/* Limited Time Offer Badge */}
                                    {/* {(product.discount > 0 || (product.freeAccessories && product.freeAccessories.length > 0)) && (
                                        <div className="absolute top-2 right-2 bg-red-600/90 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-bold flex items-center bounce-animation">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Limited
                                        </div>
                                    )} */}
                                    
                                    {/* Quick Action Buttons - Appear on Hover */}
                                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 backdrop-blur-sm py-3 px-4 flex justify-between items-center transition-all duration-300 translate-y-full opacity-0 group-hover:translate-y-0 group-hover:opacity-100">
                                        <button 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                // handleAddToCart function
                                            }}
                                            className="bg-white text-[#DA9687] hover:bg-[#DA9687] hover:text-white p-2 rounded-full transition-colors duration-300"
                                            aria-label="Add to cart"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 6M7 13l-1.5 6m0 0H17M7 19v2a2 2 0 002 2h6a2 2 0 002-2v-2" />
                                            </svg>
                                        </button>
                                        <Link 
                                            to={`/product/${product.id}`}
                                            className="bg-white text-[#514B60] hover:bg-[#514B60] hover:text-white p-2 rounded-full transition-colors duration-300"
                                            aria-label="Quick view"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        </Link>
                                    </div>
                                </div>
                                
                                {/* Product Details - Flexible content area */}
                                <div className="p-3 sm:p-4 flex flex-col flex-grow">
                                    {/* Product Info - Flexible grow area */}
                                    <div className="flex-grow">
                                        <h3 className="text-base sm:text-lg md:text-xl font-serif text-[#614E42] group-hover:text-[#8E9775] transition-colors mb-1 sm:mb-2 leading-tight">
                                            {product.name}
                                        </h3>
                                        {/* <p className="text-[#8E9775] text-sm mb-2 sm:mb-3 line-clamp-2">{product.category}</p> */}
                                        
                                        {/* Additional product info if available */}
                                        {/* {product.description && (
                                            <p className="text-gray-600 text-xs sm:text-sm mb-2 line-clamp-2">
                                                {product.description}
                                            </p>
                                        )} */}
                                    </div>
                                    
                                    {/* Price and Rating - Fixed at bottom */}
                                    <div className="mt-auto">
                                        {/* Price with Discount Logic */}
                                        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center sm:gap-0 mb-2">
                                            <div className="flex items-baseline">
                                                {product.discount > 0 ? (
                                                    <>
                                                        <span className="text-[#DA9687] font-semibold text-lg sm:text-xl">
                                                            ₹{product.salePrice || (product.cost * (1 - product.discount / 100)).toFixed(2)}
                                                        </span>
                                                        <span className="text-gray-500 text-sm line-through ml-2">
                                                            ₹{product.cost.toFixed(2)}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span className="text-[#DA9687] font-semibold text-lg sm:text-xl">
                                                        ₹{product.cost.toFixed(2)}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center justify-start sm:justify-end">
                                                {[...Array(5)].map((_, index) => (
                                                    <span
                                                        key={index}
                                                        className={`text-sm sm:text-base ${index < product.rating ? 'text-amber-500' : 'text-gray-300'}`}
                                                    >
                                                        ★
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        
                                        {/* Status Messages */}
                                        <div className="space-y-1 mb-3">
                                            {/* Low Stock Warning */}
                                            {product.isLowStock && (
                                                <div className="text-xs text-red-600 flex items-center flash-animation">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                                                    </svg>
                                                    <span>Only {product.randomStock || product.stock} left in stock</span>
                                                </div>
                                            )}
                                            
                                            {/* Free Accessories Notice */}
                                            {product.freeAccessories && product.freeAccessories.length > 0 && (
                                                <div className="text-xs text-orange-600 flex items-center">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                                    </svg>
                                                    <span>Free accessories included</span>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Dynamic Shop Now Button - Always at bottom */}
                                        <button 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                // Navigate to product or add to cart
                                            }}
                                            className={`w-full ${
                                                product.isLowStock || product.discount > 0 || (product.freeAccessories && product.freeAccessories.length > 0) ? 
                                                'bg-red-600 text-white hover:bg-red-700' : 
                                                'bg-white border border-[#DA9687] text-[#DA9687] hover:bg-[#DA9687] hover:text-white'
                                            } py-2 sm:py-2.5 rounded-md transition-colors duration-300 text-sm sm:text-base font-medium`}
                                        >
                                            {product.isLowStock ? 'Buy Now - Limited Stock!' : 'Shop Now'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Link>

                    )) : (
                        <div className="col-span-full text-center py-16">
                            <div className="text-7xl mb-4">🕊️</div>
                            <h3 className="text-2xl font-serif text-[#614E42] mb-2">Your spiritual items await discovery</h3>
                            <p className="text-[#8E9775]">Try another search or category</p>
                        </div>
                    )}
                </div>

                {/* Inspirational Quote */}
                <div className="text-center mb-12">
                    <p className="italic text-[#614E42] text-xl">"The spiritual journey is the unlearning of fear and the acceptance of love."</p>
                    <p className="text-[#8E9775] mt-2">— Marianne Williamson</p>
                </div>
                
                <div className="text-center mb-12">
                    <h1 className='text-3xl text-[#D88E7D] stylefont mb-2'>Sacred Collection</h1>
                    <h2 className="text-5xl mb-6 headerstyle text-[#614E42]">Divine Products</h2>
                    

                </div>

                {/* Spiritual Benefits */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
                    <div className="bg-white/70 backdrop-blur-sm p-6 rounded-lg text-center">
                        <div className="text-4xl mb-3 text-[#8E9775]">✨</div>
                        <h3 className="text-xl text-[#614E42] font-serif mb-2">Divine Quality</h3>
                        <p className="text-[#614E42]">Each item is ethically sourced and created with intentional purpose</p>
                    </div>
                    
                    <div className="bg-white/70 backdrop-blur-sm p-6 rounded-lg text-center">
                        <div className="text-4xl mb-3 text-[#8E9775]">🕉️</div>
                        <h3 className="text-xl text-[#614E42] font-serif mb-2">Sacred Energy</h3>
                        <p className="text-[#614E42]">Products blessed with positive vibrations to elevate your spiritual practice</p>
                    </div>
                    
                    <div className="bg-white/70 backdrop-blur-sm p-6 rounded-lg text-center">
                        <div className="text-4xl mb-3 text-[#8E9775]">🌿</div>
                        <h3 className="text-xl text-[#614E42] font-serif mb-2">Healing Intent</h3>
                        <p className="text-[#614E42]">Carefully crafted to support your healing journey and spiritual growth</p>
                    </div>
                </div>


                
                {/* Testimonials */}
                <div className="mt-20">
                    <h2 className="text-3xl font-serif text-[#614E42] text-center mb-10">Soul Testimonials</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div className="bg-white/70 backdrop-blur-sm p-6 rounded-lg">
                            <div className="text-[#8E9775] text-5xl mb-4">"</div>
                            <p className="italic text-[#614E42] mb-4">The prayer beads I purchased have truly enhanced my meditation practice. I can feel the sacred energy within them.</p>
                            <p className="font-medium text-[#8E9775]">— Sarah J.</p>
                        </div>
                        
                        <div className="bg-white/70 backdrop-blur-sm p-6 rounded-lg">
                            <div className="text-[#8E9775] text-5xl mb-4">"</div>
                            <p className="italic text-[#614E42] mb-4">Since using the cleansing kit, my home feels lighter and more peaceful. The quality of these spiritual items is exceptional.</p>
                            <p className="font-medium text-[#8E9775]">— Michael T.</p>
                        </div>
                        
                        <div className="bg-white/70 backdrop-blur-sm p-6 rounded-lg">
                            <div className="text-[#8E9775] text-5xl mb-4">"</div>
                            <p className="italic text-[#614E42] mb-4">The incense from this store has a purity I haven't found elsewhere. It's become an essential part of my daily spiritual practice.</p>
                            <p className="font-medium text-[#8E9775]">— Priya M.</p>
                        </div>
                    </div>
                </div>
                
                {/* Call to Action */}
                <div className="mt-20 text-center">
                    <h2 className="text-3xl font-serif text-[#614E42] mb-4">Begin Your Spiritual Journey Today</h2>
                    <p className="text-[#8E9775] max-w-2xl mx-auto mb-8">
                        Each product is designed to enhance your connection to the divine and support your spiritual growth
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Store;